import React, { useState, useCallback } from "react";
import Header from "../components/header";
import UpdateStockDetails from "./updateStockDetails";
import "./updateHistoryList.css";
import { get } from "../config/api";
import {
  formatCycleInfoDate,
  getCalendarInfo,
  parseAndNormalizeDate,
  parseAndNormalizeEndOfDay,
} from "../helper-functions";
import { useAppDispatch } from "../store/hooks";
import {
  UPDATE_HISTORY_TITLES,
  UPDATE_HISTORY_MESSAGES,
} from "../constants/textconstants";
import { RequestStockData, RequestUpdateHistory } from "../interfaces/types";

interface UpdateHistoryListProps {
  data?: RequestUpdateHistory[];
  onBack?: () => void;
  onNavigateToHome?: () => void;
  onNavigateToCCMHome?: () => void;
  retailerId?: string;
  retailerInfo?: { customerName: string; customerId: string } | null;
  userRole?: string;
  onStockDataUpdated?: (
    stockData: RequestStockData[],
    entry: RequestUpdateHistory
  ) => void;
  selectedEntryFromHome?: RequestUpdateHistory | null; // Add this prop to handle navigation from home
}

export const UpdateHistoryList: React.FC<UpdateHistoryListProps> = ({
  data = [],
  onBack,
  onNavigateToHome,
  onNavigateToCCMHome,
  retailerId,
  retailerInfo,
  userRole,
  onStockDataUpdated,
  selectedEntryFromHome,
}) => {
  const [selectedEntry, setSelectedEntry] =
    useState<RequestUpdateHistory | null>(null);
  const [showStockDetails, setShowStockDetails] = useState<boolean>(false);
  const [showEditStockDetails, setShowEditStockDetails] =
    useState<boolean>(false);
  const [stockData, setStockData] = useState<RequestStockData[]>([]);
  const [isLoadingStockData, setIsLoadingStockData] = useState<boolean>(false);

  // Access calendarInfo from localStorage
  const calendarInfo = getCalendarInfo();
  console.log("Retrieved calendar info from localStorage:", calendarInfo);

  // Calculate total quantity for stock details view based on selected date
  const stockDetailsTotalQuantity = stockData.reduce(
    (sum: number, item: RequestStockData) => sum + item.stockQuantity,
    0
  );

  // Common function to check if alert/edit should be shown for current cycle
  const shouldShowAlert = (entry: RequestUpdateHistory | null, entryIndex: number) => {
    if (entryIndex !== 0) return false;

    const currentCycleStartDate = calendarInfo?.currentCycleStartDate
      ? new Date(calendarInfo.currentCycleStartDate)
      : null;
    const currentCycleDueDate = calendarInfo?.currentCycleDueDate
      ? new Date(calendarInfo.currentCycleDueDate)
      : null;
    const currentCycleRetailerDueDate = calendarInfo?.currentCycleRetailerDueDate
      ? new Date(calendarInfo.currentCycleRetailerDueDate)
      : null;

    // Check if entry's stockCycle matches current cycle start date
    const entryDate = entry?.stockCycle ? new Date(entry.stockCycle) : null;
    const isCurrentCycle =
      currentCycleStartDate &&
      entryDate &&
      currentCycleStartDate.toDateString() === entryDate.toDateString();

    // Only show alert if this entry is for the current stock cycle
    if (!isCurrentCycle) return false;

    if (userRole === "retailer") {
      return (
        currentCycleStartDate &&
        currentCycleRetailerDueDate &&
        parseAndNormalizeDate(currentCycleStartDate?.toISOString() || "") <= new Date() &&
        new Date() <= parseAndNormalizeEndOfDay(currentCycleRetailerDueDate?.toISOString() || "")
      );
    } else {
      return (
        currentCycleStartDate &&
        currentCycleDueDate &&
        parseAndNormalizeDate(currentCycleStartDate?.toISOString() || "") <= new Date() &&
        new Date() <= parseAndNormalizeEndOfDay(currentCycleDueDate?.toISOString() || "")
      );
    }
  };

  const handleBackClick = () => {
    if (showEditStockDetails) {
      setShowEditStockDetails(false);
    } else if (showStockDetails) {
      setShowStockDetails(false);
      setSelectedEntry(null);
      setStockData([]); // Reset stock data when going back
    } else if (onBack) {
      onBack();
    } else {
      console.log("Back to previous page");
    }
  };

  const handleEntryClick = useCallback(
    async (entry: RequestUpdateHistory, index: number) => {
      console.log("handleEntryClick called with entry:", entry);
      setSelectedEntry(entry);
      setShowStockDetails(true);

      // Clear previous stock data and fetch new data for selected date
      setStockData([]);
      setIsLoadingStockData(true);

      try {
        // First try to get stock data from the entry itself
        if (entry.stockData && entry.stockData.length > 0) {
          console.log(
            "Using stock data from entry.stockData:",
            entry.stockData
          );
          setStockData(entry.stockData);
        } else if (entry.productStocks && entry.productStocks.length > 0) {
          // Extract stock data from productStocks array
          console.log(
            "Extracting stock data from entry.productStocks:",
            entry.productStocks
          );
          const extractedStockData: RequestStockData[] =
            entry.productStocks.map((item: any) => ({
              productName: item.productName || "Unknown Product",
              productDisplayName: item.productDisplayName || item.productName,
              stockQuantity: item.stockQuantity || 0,
            }));
          setStockData(extractedStockData);
        } else {
          // Fallback: fetch from customer product stocks API
          if (!retailerId) {
            console.error("No retailerId available for API fetch");
            setStockData([]);
            setIsLoadingStockData(false);
            return;
          }

          const stockCycle = entry.stockCycle;

          // Fetch from customer product stocks API
          const response = await get(
            `/api/v1/customers/${retailerId}/product-stocks`
          );

          if (
            response &&
            (Array.isArray(response) ? response.length > 0 : response)
          ) {
            const historyData = Array.isArray(response) ? response : [response];

            // Filter based on stockCycle if provided
            const filteredHistory = stockCycle
              ? historyData.filter(
                  (historyItem: any) =>
                    historyItem.stockCycle === stockCycle ||
                    historyItem.cycleDate === stockCycle
                )
              : historyData;

            // Use the first matching item or the first item if no filter match
            const selectedApiData =
              filteredHistory.length > 0 ? filteredHistory[0] : historyData[0];

            if (
              selectedApiData &&
              selectedApiData.productStocks &&
              selectedApiData.productStocks.length > 0
            ) {
              const extractedStockData: RequestStockData[] =
                selectedApiData.productStocks.map((product: any) => ({
                  productName: product.productName || "Unknown Product",
                  productDisplayName:
                    product.productDisplayName || product.productName,
                  stockQuantity: product.stockQuantity || 0,
                }));
              setStockData(extractedStockData);
            } else {
              console.warn("No productStocks found in API response");
              setStockData([]);
            }
          } else {
            setStockData([]);
          }
        }
      } catch (error) {
        console.error("Error in handleEntryClick:", error);
        setStockData([]);
      } finally {
        setIsLoadingStockData(false);
      }
    },
    [retailerId]
  );

  const handleEditStock = () => {
    console.log("Edit stock for date:", selectedEntry?.stockCycle);
    setShowEditStockDetails(true);
  };

  const handleSaveStockDetails = (updatedStockData: any[]) => {
    console.log("Stock details saved:", updatedStockData);

    // Update the local stockData state
    setStockData(updatedStockData);

    // Update the selected entry with the new stock data
    if (selectedEntry) {
      const updatedEntry = {
        ...selectedEntry,
        stockData: updatedStockData,
        // Also update products if it exists
        products: updatedStockData.map((item) => ({
          stockRecordId: item.stockRecordId,
          productName: item.productName,
          productDisplayName: item.productDisplayName || item.productName,
          stockQuantity: item.stockQuantity,
        })),
      };
      setSelectedEntry(updatedEntry);

      // Notify parent component of the stock data update
      if (onStockDataUpdated) {
        onStockDataUpdated(updatedStockData, updatedEntry);
      }
    }

    setShowEditStockDetails(false);
  };
  return (
    <div >
      {showEditStockDetails ? (
        // Edit Stock Details View
        <UpdateStockDetails
          stockData={stockData.map((item) => ({
            productName: item.productName,
            productDisplayName: item.productDisplayName || item.productName,
            stockQuantity: item.stockQuantity,
          }))}
          selectedDate={selectedEntry?.stockCycle}
          onBack={() => setShowEditStockDetails(false)}
          onSave={handleSaveStockDetails}
          onNavigateToHome={onNavigateToHome}
          onNavigateToCCMHome={onNavigateToCCMHome}
          customerId={retailerId}
          userRole={userRole}
        />
      ) : (
        <div className="page-container history-page-container">
          {/* Header Section */}
          <Header
            title={
              showStockDetails
                ? (() => {
                    const entryIndex = data.indexOf(selectedEntry!);
                    const showAlert = shouldShowAlert(selectedEntry, entryIndex);
                    const dateFormat = showAlert ? 'MMM DD, YYYY' : 'MMM DD';
                    const dateStr = formatCycleInfoDate(selectedEntry?.stockCycle, dateFormat);
                    return showAlert
                      ? dateStr 
                      : `${dateStr} ${UPDATE_HISTORY_TITLES.DETAILS_SUFFIX}`;
                  })()
                : userRole === "retailer"
                ? UPDATE_HISTORY_TITLES.UPDATE_HISTORY
                : UPDATE_HISTORY_TITLES.STOCK_HISTORY
            }
            onBackClick={handleBackClick}
          />

          {/* Retailer Info Section */}
          {retailerInfo && (
            <div className="retailer-info-section">
              <div className="retailer-info-card">
                <div className="retailer-info-header">
                  <span className="retailer-info-name">
                    {retailerInfo.customerName}
                  </span>
                </div>
                <div className="retailer-info-id">
                  {UPDATE_HISTORY_MESSAGES.ID_PREFIX} {retailerInfo.customerId}
                </div>
              </div>
            </div>
          )}

          <div className="history-content-section">
            {showStockDetails ? (
              <>
                <div className="stat-label-list">
                  {UPDATE_HISTORY_MESSAGES.STOCK_AS_OF}{" "}
                  {`${formatCycleInfoDate(
                    selectedEntry?.stockCycle,
                    "DD MMM, YYYY"
                  )}`}
                </div>

                {/* Stock Details View */}
                <div className="stock-details-view">
                  {isLoadingStockData ? (
                    <div className="loading-state">
                      <span className="loading-icon">⏳</span>
                      <p className="loading-text">
                        {UPDATE_HISTORY_MESSAGES.LOADING_STOCK_DETAILS}
                      </p>
                    </div>
                  ) : stockData.length === 0 ? (
                    <div className="empty-state">
                      <span className="empty-icon">📦</span>
                      <p className="empty-text">
                        {UPDATE_HISTORY_MESSAGES.NO_STOCK_DETAILS_AVAILABLE}{" "}
                        {selectedEntry?.stockCycle}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="stock-details-list">
                        {stockData.map(
                          (item: RequestStockData, index: number) => (
                            <div
                              key={item.productName}
                              className="stock-detail-item"
                            >
                              <span className="stock-detail-id">
                                {item.productDisplayName || item.productName}
                              </span>
                              <span className="stock-detail-quantity">
                                {item.stockQuantity}{" "}
                                {UPDATE_HISTORY_MESSAGES.KGS}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                      <div className="stock-total">
                        <span>{UPDATE_HISTORY_MESSAGES.TOTAL}</span>
                        <span>
                          {stockDetailsTotalQuantity}{" "}
                          {UPDATE_HISTORY_MESSAGES.KGS}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              // History List View
              <div className="history-items">
                {data.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-icon">📋</span>
                    <p className="empty-text">
                      {UPDATE_HISTORY_MESSAGES.NO_UPDATE_HISTORY_FOUND}
                    </p>
                  </div>
                ) : (
                  data.map((entry, index) => (
                    <div
                      key={index}
                      className={`retailer-update-history-card ${
                        index === 0 ? "current-entry" : "historical-entry"
                      }`}
                      onClick={() => handleEntryClick(entry, index)}
                    >
                      <div>
                        <div className="update-info">
                          <div className="update-date">
                            {(() => {
                              const dateValue = entry.stockCycle;
                              return formatCycleInfoDate(
                                dateValue,
                                "MMM DD, YYYY"
                              );
                            })()}
                          </div>
                          <div className="update-quantity">
                            {entry.totalAvailableStock || 0}{" "}
                            {UPDATE_HISTORY_MESSAGES.KGS}
                          </div>
                        </div>
                        <div>
                          {shouldShowAlert(entry, index) && (
                            <div className="history-update-alert">
                              <span className="update-history-alert-icon">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                                    fill="#0095A5"
                                  />
                                </svg>
                              </span>
                              <span className="update-history-alert-text">
                                {userRole === "retailer"
                                  ? `${
                                      UPDATE_HISTORY_MESSAGES.YOU_CAN_ADD_EDIT_DETAILS
                                    } ${formatCycleInfoDate(
                                      calendarInfo?.currentCycleRetailerDueDate,
                                      "DD MMM YYYY"
                                    )}`
                                  : `${
                                      UPDATE_HISTORY_MESSAGES.YOU_CAN_ADD_EDIT_CCM
                                    } ${formatCycleInfoDate(
                                      calendarInfo?.currentCycleDueDate,
                                      "DD MMM YYYY"
                                    )}`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Footer with Edit Stock button - show based on user role and cycle dates */}
          {(() => {
            if (!showStockDetails) return null;
            const entryIndex = data.indexOf(selectedEntry!);
            
            return (
              shouldShowAlert(selectedEntry, entryIndex) && (
                <div className="edit-stock-footer">
                  <button className="edit-stock-btn" onClick={handleEditStock}>
                    {UPDATE_HISTORY_MESSAGES.EDIT_STOCK_DETAILS}
                  </button>
                </div>
              )
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default UpdateHistoryList;
